/**
 * Distribution-Agnostic Fit Diagnostics Module
 *
 * Computes diagnostic information about a dataset that may explain
 * why goodness-of-fit tests appear poor, without changing any existing
 * statistics or conclusions.
 *
 * Phase 1: Explanatory diagnostics only - no distribution fitting logic.
 */

/**
 * Compute the median of a numeric array
 * @param {number[]} arr - Array of numbers (will be sorted)
 * @returns {number} The median value
 */
function median(arr) {
    if (arr.length === 0) return NaN;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0
        ? sorted[mid]
        : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Compute discretization/measurement-resolution diagnostics
 * @param {number[]} data - Array of numeric values
 * @returns {Object} Discretization diagnostics
 */
function computeDiscretizationDiagnostics(data) {
    const n = data.length;
    if (n === 0) {
        return {
            unique_count: 0,
            unique_ratio: 0,
            discretizationLevel: 'none',
            quantized: false,
            quantizationStep: null
        };
    }

    // Get unique values
    const uniqueValues = [...new Set(data)];
    const unique_count = uniqueValues.length;
    const unique_ratio = unique_count / n;

    // Determine discretization level
    let discretizationLevel;
    if (unique_ratio <= 0.20) {
        discretizationLevel = 'severe';
    } else if (unique_ratio <= 0.35) {
        discretizationLevel = 'moderate';
    } else {
        discretizationLevel = 'none';
    }

    // Detect quantization by checking if data aligns to a common step
    let quantized = false;
    let quantizationStep = null;

    if (unique_count >= 2) {
        // Sort unique values and compute successive differences
        const sortedUnique = [...uniqueValues].sort((a, b) => a - b);
        const differences = [];
        for (let i = 1; i < sortedUnique.length; i++) {
            const diff = sortedUnique[i] - sortedUnique[i - 1];
            if (diff > 1e-10) { // Filter out near-zero differences
                differences.push(diff);
            }
        }

        if (differences.length > 0) {
            // Find minimum nonzero step
            const minStep = Math.min(...differences);

            // Check if most data aligns to this step
            // For each data point, check if it's within epsilon of a multiple of minStep
            const epsilon = minStep * 0.01; // 1% tolerance
            const baseValue = sortedUnique[0];

            let alignedCount = 0;
            for (const value of data) {
                const offsetFromBase = value - baseValue;
                const nearestMultiple = Math.round(offsetFromBase / minStep) * minStep;
                if (Math.abs(offsetFromBase - nearestMultiple) <= epsilon) {
                    alignedCount++;
                }
            }

            // If more than 80% align to the step, data is quantized
            if (alignedCount / n > 0.80) {
                quantized = true;
                quantizationStep = minStep;
            }
        }
    }

    return {
        unique_count,
        unique_ratio,
        discretizationLevel,
        quantized,
        quantizationStep
    };
}

/**
 * Compute special-cause/outlier-suspected diagnostics using robust methods
 * Distribution-agnostic: uses median and MAD instead of mean and std dev
 * @param {number[]} data - Array of numeric values
 * @returns {Object} Outlier diagnostics
 */
function computeOutlierDiagnostics(data) {
    const n = data.length;

    // Only perform outlier detection if n >= 20
    if (n < 20) {
        return {
            outlierSuspected: false,
            extremeCount: 0,
            strongCount: 0,
            outlierIndices: {
                extreme: [],
                strong: []
            },
            robustStats: null
        };
    }

    // Compute median
    const med = median(data);

    // Compute MAD (Median Absolute Deviation)
    const absoluteDeviations = data.map(x => Math.abs(x - med));
    const mad = median(absoluteDeviations);

    // If MAD is 0 or very small, we can't reliably detect outliers
    if (mad < 1e-10) {
        return {
            outlierSuspected: false,
            extremeCount: 0,
            strongCount: 0,
            outlierIndices: {
                extreme: [],
                strong: []
            },
            robustStats: { median: med, mad: mad, robust_sigma: 0 }
        };
    }

    // Robust sigma estimate (for normal distribution, MAD * 1.4826 ≈ std dev)
    const robust_sigma = 1.4826 * mad;

    // Compute robust z-scores and flag outliers
    const extremeIndices = [];
    const strongIndices = [];

    for (let i = 0; i < n; i++) {
        const robust_z = Math.abs(data[i] - med) / robust_sigma;

        if (robust_z >= 6) {
            extremeIndices.push(i);
        } else if (robust_z >= 5) {
            strongIndices.push(i);
        }
    }

    // Set outlierSuspected = true only if there are 1-2 extreme points
    // (more suggests systematic heavy tails rather than special cause)
    const extremeCount = extremeIndices.length;
    const strongCount = strongIndices.length;
    const outlierSuspected = extremeCount >= 1 && extremeCount <= 2;

    return {
        outlierSuspected,
        extremeCount,
        strongCount,
        outlierIndices: {
            extreme: extremeIndices,
            strong: strongIndices
        },
        robustStats: {
            median: med,
            mad: mad,
            robust_sigma: robust_sigma
        }
    };
}

/**
 * Compute large-sample sensitivity diagnostics
 * @param {number} n - Sample size
 * @returns {Object} Sensitivity diagnostics
 */
function computeSensitivityDiagnostics(n) {
    let sensitivityLevel;

    if (n >= 1000) {
        sensitivityLevel = 'very_high';
    } else if (n >= 300) {
        sensitivityLevel = 'high';
    } else {
        sensitivityLevel = 'normal';
    }

    return {
        sensitivityLevel
    };
}

/**
 * Main function: Compute all fit diagnostics for a dataset
 * @param {number[]} data - Array of numeric values
 * @returns {Object} Complete diagnostics object
 */
export function computeFitDiagnostics(data) {
    // Filter out non-numeric values
    const numericData = data.filter(x => typeof x === 'number' && !isNaN(x) && isFinite(x));
    const n = numericData.length;

    if (n === 0) {
        return {
            n: 0,
            discretizationLevel: 'none',
            quantized: false,
            unique_ratio: 0,
            unique_count: 0,
            quantizationStep: null,
            outlierSuspected: false,
            extremeCount: 0,
            strongCount: 0,
            outlierIndices: { extreme: [], strong: [] },
            robustStats: null,
            sensitivityLevel: 'normal'
        };
    }

    // Compute all diagnostic categories
    const discretization = computeDiscretizationDiagnostics(numericData);
    const outliers = computeOutlierDiagnostics(numericData);
    const sensitivity = computeSensitivityDiagnostics(n);

    return {
        n,
        // Discretization diagnostics
        discretizationLevel: discretization.discretizationLevel,
        quantized: discretization.quantized,
        unique_ratio: discretization.unique_ratio,
        unique_count: discretization.unique_count,
        quantizationStep: discretization.quantizationStep,
        // Outlier diagnostics
        outlierSuspected: outliers.outlierSuspected,
        extremeCount: outliers.extremeCount,
        strongCount: outliers.strongCount,
        outlierIndices: outliers.outlierIndices,
        robustStats: outliers.robustStats,
        // Sensitivity diagnostics
        sensitivityLevel: sensitivity.sensitivityLevel
    };
}

/**
 * Get diagnostic chips to display based on diagnostics results
 * Returns up to 2 chips in priority order
 * @param {Object} diagnostics - Result from computeFitDiagnostics
 * @returns {Array} Array of chip objects with id, label, severity, and tooltip
 */
export function getDiagnosticChips(diagnostics) {
    const chips = [];

    // Priority 1: Outlier suspected
    if (diagnostics.outlierSuspected) {
        chips.push({
            id: 'outlier',
            label: `${diagnostics.extremeCount} potential special-cause point${diagnostics.extremeCount > 1 ? 's' : ''}`,
            severity: 'warning',
            tooltip: 'Potential special-cause point(s) detected using a robust, distribution-agnostic screen (median/MAD). Isolated extreme points can cause goodness-of-fit tests to reject otherwise well-behaved data; investigate special cause before rejecting a model.'
        });
    }

    // Priority 2: Discretization (moderate/severe) or quantization
    if (diagnostics.discretizationLevel === 'severe' || diagnostics.discretizationLevel === 'moderate' || diagnostics.quantized) {
        let label;
        if (diagnostics.quantized) {
            label = 'Quantized data detected';
        } else if (diagnostics.discretizationLevel === 'severe') {
            label = 'Severe discretization';
        } else {
            label = 'Moderate discretization';
        }

        chips.push({
            id: 'discretization',
            label: label,
            severity: diagnostics.discretizationLevel === 'severe' ? 'warning' : 'info',
            tooltip: 'Quantized measurement resolution or many tied values detected. Continuous distribution goodness-of-fit p-values may be overly conservative when data are discretized.'
        });
    }

    // Priority 3: Large sample sensitivity
    if (diagnostics.sensitivityLevel === 'very_high' || diagnostics.sensitivityLevel === 'high') {
        chips.push({
            id: 'sample_size',
            label: diagnostics.sensitivityLevel === 'very_high' ? 'Very large sample (n≥1000)' : 'Large sample (n≥300)',
            severity: 'info',
            tooltip: 'Large sample size; goodness-of-fit tests are very sensitive and may flag trivial departures from the theoretical distribution that have no practical impact on capability estimates.'
        });
    }

    // Return at most 2 chips
    return chips.slice(0, 2);
}

export default computeFitDiagnostics;
